// component/ChatHeader.jsx
// ------------------------
// The coloured header bar shown at the top of the chat panel once an agent
// is selected.  It contains:
//   • A back-arrow button (only for non-bot agents that need file selection)
//   • The agent's emoji avatar, name, and description
//   • A ⋯ menu with a "Clear Chat" option
//
// Props:
//   agent              – The active agent key string (e.g. "knowledgebase_agent")
//   botName            – Display name from URL params (e.g. "iKomet")
//   bottheme           – Tailwind class for the header background colour
//   showMenu           – Boolean: whether the ⋯ dropdown is open
//   setShowMenu        – Setter to toggle the ⋯ dropdown
//   onClearChat        – Called when the user clicks "Clear Chat"
//   onBackToSelector   – Called when the user clicks the back arrow

import React from 'react';
import { IoChevronBack } from 'react-icons/io5';
import agents from '../config/agents';
import { isIdoksNewTheme } from '../utils/hostTheme';

// The redesign's mark: a line glyph in the same family as the icons iDoks uses in the
// app itself, in place of the emoji. An avatar the admin uploaded still wins over it —
// being asked for a skin is not a licence to overrule something stated outright.
const NewThemeMark = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7.5A2.5 2.5 0 0 0 5 5.5v13A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V8z" />
    <path d="M14 3v5h5" />
    <circle cx="11.2" cy="13.6" r="2.6" />
    <path d="m13.2 15.6 2 2" />
  </svg>
);

// The broom emoji the menu used renders as a smear at this size on Windows, and an
// emoji is the wrong register for a menu command anyway — a line glyph, same family
// as the mark above, says "a fresh thread" without the picture.
const NewChatMark = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.5 11.7a7.7 7.7 0 0 1-11.2 6.9l-4.8 1.4 1.4-4.5A7.7 7.7 0 1 1 20.5 11.7Z" />
    <path d="M12.6 8.9v5.2" />
    <path d="M10 11.5h5.2" />
  </svg>
);

// Developer-only. CRA bakes NODE_ENV in at build time: `npm start` is "development", every
// `npm run build` (staging, production) is "production" - so this is false, and the button
// below is not rendered at all, in anything that ships.
const IS_DEV_BUILD = process.env.NODE_ENV === 'development';

// A small node-graph glyph: one node feeding three, the shape of the map it opens.
const AgentMapMark = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="5" r="2.2" />
    <circle cx="5" cy="19" r="2.2" />
    <circle cx="12" cy="19" r="2.2" />
    <circle cx="19" cy="19" r="2.2" />
    <path d="M12 7.2v4.3M12 11.5 5.6 17M12 11.5v5.3M12 11.5 18.4 17" />
  </svg>
);

// A tray with a downward arrow: "save this conversation as a file".
const ExportMark = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 4v11" />
    <path d="m7.5 11 4.5 4.5 4.5-4.5" />
    <path d="M5 19.5h14" />
  </svg>
);

const ChatHeader = ({ agent, botName, botDescription, botImage, bottheme, showMenu, setShowMenu, onClearChat, onBackToSelector, onViewAgentMap, onExportChat }) => {
  const agentConfig = agents[agent];
  const newTheme = isIdoksNewTheme();

  return (
    <div className={`${bottheme} caw-header text-white p-4 flex items-center`}>

      {/* ------------------------------------------------------------------ */}
      {/* Back button — only shown for agents that require source files        */}
      {/* (isbot agents are always-on; tool agents need the user to go back   */}
      {/*  and pick files first)                                               */}
      {/* ------------------------------------------------------------------ */}
      {agentConfig?.isbot !== true && (
        <button
          className="caw-back absolute top-4 left-4 text-white-700 hover:text-white-900"
          onClick={onBackToSelector}
        >
          <IoChevronBack size={20} />
        </button>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Agent avatar + name + description                                   */}
      {/* ------------------------------------------------------------------ */}
      <div className={`caw-header-main ${agentConfig?.isbot !== true ? 'ml-8' : 'ml-0'} flex gap-3 items-start`}>
        {/* Avatar: headerImage > initials (for custom_agent) > default emoji */}
        {botImage ? (
          <img
            className="caw-header-avatar caw-header-avatar-img"
            src={botImage}
            alt={botName || agentConfig?.name || 'Bot'}
            style={{
              width: 40, height: 40, borderRadius: '50%',
              objectFit: 'cover', flexShrink: 0,
              border: '2px solid rgba(255,255,255,0.4)',
            }}
          />
        ) : agent === 'custom_agent' ? (
          /* Initials avatar — derived from bot name (e.g. "Larken Agent" → "LA") */
          <div className="caw-header-avatar" style={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(255,255,255,0.2)',
            border: '2px solid rgba(255,255,255,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '0.5px',
            userSelect: 'none',
          }}>
            {(botName || agentConfig?.name || 'AI')
              .split(' ').filter(Boolean).slice(0, 2)
              .map(w => w[0].toUpperCase()).join('')}
          </div>
        ) : (
          <div className={`caw-header-avatar w-10 h-10 text-xl flex items-center justify-center rounded-full ${newTheme ? '' : agentConfig.color}`}>
            {newTheme ? <NewThemeMark /> : agentConfig.emoji}
          </div>
        )}
        <div className="caw-header-text">
          {agent === 'custom_agent' ? (
            <>
              <h2 className="caw-header-title text-md font-bold text-white-900 leading-tight">{botName || agentConfig.name}</h2>
              <p className="caw-header-desc text-xs text-blue-100">{botDescription || agentConfig.description}</p>
            </>
          ) : (agent === 'idoks_agent' || agent === 'knowledgebase_agent') ? (
            <>
              <h2 className="caw-header-title text-md font-bold text-white-900 leading-tight">{botName} {agentConfig.name}</h2>
              <p className="caw-header-desc text-xs text-blue-100">{agentConfig.description}</p>
            </>
          ) : (
            <>
              <h2 className="caw-header-title text-md font-bold text-white-900 leading-tight">{agentConfig.name}</h2>
              <p className="caw-header-desc text-xs text-blue-100">{agentConfig.description}</p>
            </>
          )}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* DEV-only: open the agent map (instructions, memory, tools)       */}
        {/* ---------------------------------------------------------------- */}
        {IS_DEV_BUILD && onViewAgentMap && (
          <button
            type="button"
            onClick={onViewAgentMap}
            title="Agent Map"
            aria-label="Show agent map (dev only)"
            className="caw-header-devmap text-white p-2"
            style={{ position: 'absolute', top: 0, right: 84 }}
          >
            <AgentMapMark />
            <span style={{
              position: 'absolute', top: 2, right: -2,
              background: '#dc2626', color: '#fff',
              fontSize: 8, fontWeight: 800, lineHeight: 1,
              padding: '2px 3px', borderRadius: 4, letterSpacing: '0.04em',
            }}>DEV</span>
          </button>
        )}

        {/* DEV-only: save this conversation + every Answer Trail as a Markdown file */}
        {IS_DEV_BUILD && onExportChat && (
          <button
            type="button"
            onClick={onExportChat}
            title="Export chat"
            aria-label="Export this conversation with answer trails (dev only)"
            className="caw-header-devexport text-white p-2"
            style={{ position: 'absolute', top: 0, right: 122 }}
          >
            <ExportMark />
            <span style={{
              position: 'absolute', top: 2, right: -2,
              background: '#dc2626', color: '#fff',
              fontSize: 8, fontWeight: 800, lineHeight: 1,
              padding: '2px 3px', borderRadius: 4, letterSpacing: '0.04em',
            }}>DEV</span>
          </button>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* ⋯ three-dot menu (top-right of the header)                       */}
        {/* ---------------------------------------------------------------- */}
        <div className="caw-header-menu absolute top-0 right-10">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="caw-header-menu-btn text-white-500 hover:text-white-700 p-2"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <circle cx="5" cy="12" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="19" cy="12" r="2" />
            </svg>
          </button>

          {/* Dropdown items */}
          {showMenu && (
            <div className="caw-menu-pop absolute top-10 right-0 bg-white border border-gray-200 rounded-md shadow-md z-50 w-max">
              <button
                onClick={onClearChat}
                className="caw-menu-item w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 whitespace-nowrap"
              >
                {newTheme ? (
                  <>
                    <NewChatMark />
                    <span>Start New Conversation</span>
                  </>
                ) : '🧹 Start New Conversation'}
              </button>
              {/* Add more menu options here if needed */}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default ChatHeader;
